import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { Err, Ok } from 'ts-results'

import { useNotifications } from 'app/application/application'
import { useConnections } from 'app/connections/application'
import { ConnectionConfig, SupportedAccount, isInstalledConnection } from 'app/connections/types'

import { Button } from 'components'

import { useConnection } from '../components/ConnectionContext'
import { mapToUrl } from '../connectionUrlMapper'
import { GenericSettings, ShopifySettings } from '../settings'
import InnkeeprSettings from '../settings/InnkeeprSettings/InnkeeprSettings'
import styles from '../settings/Settings.module.scss'

export interface SettingsProps {
  onConnectionConfigValidationUpdate: (isValidConfig: boolean) => void
}

// eslint-disable-next-line react-refresh/only-export-components
export const noSaveIfInstalled = ['innkeepr']

export const ConnectConnectionSettings = () => {
  const navigate = useNavigate()
  const connection = useConnection()
  const { t } = useTranslation('connections')

  const { createNotification, te } = useNotifications()
  const { saveConnection } = useConnections()

  const [connectionConfig, setConnectionConfig] = useState<ConnectionConfig>(connection.config)
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [isValidConfig, setIsValidConfig] = useState<boolean>(false)

  const handleSave = () => {
    setIsSaving(true)

    return saveConnection(connection, connectionConfig)
      .then((result) => {
        if (result.ok) {
          createNotification({
            message: t(
              `${isInstalledConnection(connection) ? 'update' : 'create'}.success.message`,
              {
                name: t(`available.${connection.name}.displayName`),
              },
            ),
            title: t(`${isInstalledConnection(connection) ? 'update' : 'create'}.success.title`),
            type: 'success',
          })

          void navigate(`/connections/${mapToUrl(connection.name)}/manage`)

          return Ok(undefined)
        } else {
          createNotification({
            message: result.val.message,
            title: te(`http.${result.val.code}`),
            type: 'error',
          })

          return Err(undefined)
        }
      })
      .finally(() => setIsSaving(false))
  }

  const showSaveButton = () => {
    if (!connection) return false
    if (connection.readonly) return false

    if (isInstalledConnection(connection) && noSaveIfInstalled.includes(connection.name)) {
      return false
    }

    return true
  }

  const handleConnectionConfigUpdate = useCallback(
    (key: string, value: SupportedAccount[] | string) =>
      setConnectionConfig((prev) =>
        prev ? ({ ...prev, [key]: value } as ConnectionConfig) : prev,
      ),
    [],
  )

  const handleOnShopUpdate = useCallback(
    (shop: string) => handleConnectionConfigUpdate('shop', shop),
    [handleConnectionConfigUpdate],
  )

  const handleOnTrackingUrlUpdate = useCallback(
    (trackingUrl: string) => handleConnectionConfigUpdate('trackingUrl', trackingUrl),
    [handleConnectionConfigUpdate],
  )

  const renderConnectionSettings = () => {
    switch (connection.name) {
      case 'innkeepr':
        return (
          <InnkeeprSettings
            onConnectionConfigValidationUpdate={setIsValidConfig}
            onTrackingUrlUpdate={handleOnTrackingUrlUpdate}
            connection={connection}
            trackingUrl={connection.config.trackingUrl}
          />
        )
      case 'shopify':
        return (
          <ShopifySettings
            connection={connection}
            onConnectionConfigValidationUpdate={setIsValidConfig}
            onShopUpdate={handleOnShopUpdate}
          />
        )
      default:
        return (
          <GenericSettings
            connection={connection}
            connectionConfig={connectionConfig}
            onConnectionConfigUpdate={handleConnectionConfigUpdate}
            onConnectionConfigValidationUpdate={setIsValidConfig}
          />
        )
    }
  }

  return (
    <>
      {renderConnectionSettings()}
      <div className={styles.actions}>
        <Button onClick={() => void navigate(-1)} variant="secondary">
          {t('setup.actions.back')}
        </Button>
        {showSaveButton() && (
          <Button
            disabled={!isValidConfig}
            loading={isSaving}
            onClick={(e) => {
              e.stopPropagation()

              void handleSave()
            }}
          >
            {t('setup.actions.save')}
          </Button>
        )}
      </div>
    </>
  )
}
